import * as React from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface CreatableComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  inputPlaceholder?: string;
  emptyText?: string;
  createText?: string;
  disabled?: boolean;
  className?: string;
  hideSearchIcon?: boolean;
  hideClearButton?: boolean;
  /** Set when rendered inside a modal Dialog: gives the portaled popover its
   * own focus scope so the dialog's focus trap doesn't steal the search input. */
  modal?: boolean;
  /** data-testid for the trigger button; several comboboxes share a page, so
   * each call site names its own. */
  testId?: string;
}

export function CreatableCombobox({
  value,
  onChange,
  options,
  placeholder = "Select or type...",
  inputPlaceholder,
  emptyText = "No options found.",
  createText = "Create",
  disabled,
  className,
  hideSearchIcon,
  hideClearButton,
  modal = false,
  testId = "creatable-combobox-trigger",
}: CreatableComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  const exactMatch = options.some(
    (option) => option.toLowerCase() === searchValue.toLowerCase(),
  );
  const showCreateOption = searchValue.trim() !== "" && !exactMatch;

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue === value ? "" : selectedValue);
    setOpen(false);
    setSearchValue("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const handleCreate = () => {
    if (searchValue.trim()) {
      onChange(searchValue.trim());
      setOpen(false);
      setSearchValue("");
    }
  };

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen} modal={modal}>
        <PopoverTrigger asChild>
          <Button
            data-testid={testId}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal",
              !value && "text-stone-500",
              className,
            )}
          >
            <span className="truncate">{value || placeholder}</span>
            <div className="flex items-center gap-1 ml-2">
              {value && !hideClearButton && (
                <X
                  className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                  onClick={handleClear}
                />
              )}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command shouldFilter={false}>
            <div className={hideSearchIcon ? "[&_svg]:hidden" : ""}>
              <CommandInput
                placeholder={inputPlaceholder ?? placeholder}
                value={searchValue}
                onValueChange={setSearchValue}
              />
            </div>
            <CommandList>
              {options.length === 0 && !showCreateOption ? (
                <CommandEmpty className="text-sm text-center py-2 px-4">
                  {emptyText}
                </CommandEmpty>
              ) : null}

              {options.length > 0 && (
                <CommandGroup>
                  {options
                    .filter((option) =>
                      option.toLowerCase().includes(searchValue.toLowerCase()),
                    )
                    .map((option) => (
                      <CommandItem
                        key={option}
                        value={option}
                        onSelect={() => handleSelect(option)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === option ? "opacity-100" : "opacity-0",
                          )}
                        />
                        {option}
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}

              {showCreateOption && (
                <div className="sticky bottom-0 bg-white">
                  {options.length > 0 && (
                    <div className="border-t border-stone-200" />
                  )}
                  <CommandGroup>
                    <CommandItem onSelect={handleCreate}>
                      <Plus className="mr-2 h-4 w-4" />
                      {createText} &quot;{searchValue}&quot;
                    </CommandItem>
                  </CommandGroup>
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
